import { getAlphafiConstants, getConstants, } from "../constants/index.js";
import { Transaction, } from "@mysten/sui/transactions";
import { appendOracleToLendingBridge } from "../utils/oracle.js";
import { appendLazerUpdate, fetchLazerUpdateBytes } from "../utils/lazer.js";
import { MAX_U64, } from "./types.js";
import { getAlphaReceipt, getClaimRewardInput, setPrices, } from "../utils/helper.js";
import { normalizeCoinType } from "../utils/parser.js";
import { resolveCoinPrice } from "../utils/price.js";
import { getUserPositionCapId } from "../models/position/functions.js";
import { LendingProtocol } from "../models/lendingProtocol.js";
import { Blockchain } from "../models/blockchain.js";
import { Decimal } from "decimal.js";
import { blockchainCache } from "../utils/blockchainCache.js";
import { CetusSwap } from "./cetusSwap.js";
import { buildFlashRepayTransaction } from "./flashRepay.js";
// Minimum estimated SUI reward (in MIST) to route through liquid_staking::mint.
// The stSUI contract aborts with EZeroLstMinted when the post-fee amount rounds
// to zero stSUI, so dust falls back to plain SUI handling.
const MIN_SUI_STAKE_AMOUNT = 3n; // 3 mists
/**
 * AlphaLend Client
 *
 * The main entry point for interacting with the AlphaLend protocol:
 * - Provides methods for all protocol actions (supply, borrow, withdraw, repay, claimRewards, liquidate)
 * - Handles connection to the Sui blockchain and the price oracle
 * - Manages transaction building for protocol interactions
 * - Exposes query methods for protocol state, markets, and user positions
 * - Initializes and coordinates price feed updates
 * - Automatically fetches market data on first use (lazy loading)
 */
export class AlphalendClient {
    network;
    constants;
    lendingProtocol;
    blockchain;
    cetusSwap;
    // Dynamic coin metadata properties
    coinMetadataMap = new Map();
    isInitialized = false;
    initializationPromise = null;
    /** Resolved at runtime from DEEPBOOK_UPGRADE_CAP_ID; used for dbUSDC deposit move calls. */
    deepbookPackageId = "";
    /** Tracks whether resolveLatestDeepbookPackageId has been called, independent of isInitialized. */
    deepbookPackageIdResolved = false;
    /** In-flight promise for resolveLatestDeepbookPackageId — prevents duplicate concurrent RPC calls. */
    deepbookPackageIdPromise = null;
    /**
     * Creates a new AlphaLend client instance.
     *
     * The SDK connects to Sui via GraphQL and gRPC only; it makes no JSON-RPC
     * calls.
     *
     * @param network    One of the supported Sui networks.
     * @param graphqlUrl Optional GraphQL endpoint override. If omitted, a default
     *                   public endpoint for the given `network` is used.
     * @param options    Optional prebuilt coin metadata map for offline testing,
     *                   and/or a gRPC endpoint override + auth token for
     *                   `blockchain.suiGrpcClient` (see AlphalendClientOptions).
     */
    constructor(network, graphqlUrl, options) {
        this.network = network;
        this.constants = getConstants(network);
        this.lendingProtocol = new LendingProtocol(network, graphqlUrl);
        this.blockchain = new Blockchain(network, graphqlUrl, options?.grpcUrl, options?.grpcToken);
        this.cetusSwap = new CetusSwap("mainnet");
        // If a coin metadata map is provided, use it and mark as initialized
        if (options?.coinMetadataMap) {
            this.coinMetadataMap = options.coinMetadataMap;
            this.isInitialized = true;
            this.lendingProtocol.updateCoinMetadataMap(this.coinMetadataMap);
        }
    }
    /**
     * Fetches the coin metadata map
     *
     * @returns Promise resolving to a Map<string, CoinMetadata> object
     */
    async fetchCoinMetadataMap() {
        await this.ensureInitialized();
        return this.coinMetadataMap;
    }
    async updatePricesLazer(tx, coinTypes, updateBytes) {
        if (coinTypes.length === 0)
            return;
        const [bytes, oracleInitialSharedVersion] = await Promise.all([
            updateBytes ?? fetchLazerUpdateBytes(this.constants.LAZER_PROXY_URL),
            this.blockchain.getInitialSharedVersion(this.constants.ALPHAFI_ORACLE_OBJECT_ID),
        ]);
        appendLazerUpdate(tx, this.constants.LAZER_PACKAGE_ID, this.constants.ALPHAFI_LATEST_ORACLE_PACKAGE_ID, this.constants.ALPHAFI_ORACLE_OBJECT_ID, oracleInitialSharedVersion, this.constants.LAZER_STATE_ID, bytes);
        for (const coinType of new Set(coinTypes)) {
            appendOracleToLendingBridge(tx, coinType, this.constants);
        }
    }
    async updatePrices(tx, coinTypes, updateBytes) {
        await this.updatePricesLazer(tx, coinTypes, updateBytes);
    }
    async updateAllPrices(tx, coinTypes, updateBytes) {
        await this.updatePricesLazer(tx, coinTypes, updateBytes);
    }
    /**
     * Supplies token collateral to the AlphaLend protocol
     *
     * @param params Supply parameters
     * @param params.marketId Market ID where collateral is being added
     * @param params.amount Amount to supply as collateral in base units (bigint, in mists)
     * @param params.coinType Fully qualified coin type to supply (e.g., "0x2::sui::SUI")
     * @param params.positionCapId Optional: Object ID of the position capability object
     * @param params.address Address of the user supplying collateral
     * @returns Transaction object ready for signing and execution
     */
    async supply(params) {
        const tx = params.tx ?? new Transaction();
        // Source the supply coin from address balance + coin objects (SUI handled inside).
        const supplyCoinA = this.getCoinObject(tx, params.coinType, params.address, BigInt(params.amount));
        if (params.positionCapId) {
            // Build add_collateral transaction
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.coinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                    tx.object(params.positionCapId), // Position capability
                    tx.pure.u64(params.marketId), // Market ID
                    supplyCoinA, // Coin to supply as collateral
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
                ],
            });
        }
        else {
            const positionCapId = await getUserPositionCapId(this.blockchain, params.address);
            let positionCap;
            if (positionCapId) {
                positionCap = tx.object(positionCapId);
            }
            else {
                positionCap = this.createPosition(tx);
            }
            // Build add_collateral transaction
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.coinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                    positionCap, // Position capability
                    tx.pure.u64(params.marketId), // Market ID
                    supplyCoinA, // Coin to supply as collateral
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
                ],
            });
            tx.transferObjects([positionCap], params.address);
        }
        return tx;
    }
    /**
     * True when the target market is dbUSDC (DEEPBOOK_STAKED<USDC>).
     * Used to choose Deepbook deposit path instead of Cetus swap.
     */
    isDbUsdcMarket(marketCoinType) {
        return (!!this.constants.DBUSDC_COIN_TYPE &&
            this.constants.DBUSDC_COIN_TYPE === marketCoinType);
    }
    /**
     * True when the user is paying with USDC.
     */
    isUsdcInput(inputCoinType) {
        return inputCoinType === this.constants.USDC_COIN_TYPE;
    }
    /**
     * True when Deepbook config is set (mainnet). When false, we use Cetus path only.
     */
    hasDeepbookConfig() {
        return !!(this.deepbookPackageId &&
            this.constants.DEEPBOOK_USDC_POOL_ID &&
            this.constants.DEEPBOOK_MARGIN_REGISTRY &&
            this.constants.DEEPBOOK_USDC_MARGIN_POOL_ID);
    }
    /**
     * True when we have static Deepbook IDs (for quote only). Does not require deepbookPackageId.
     * Use in getSwapQuote so SUI→dbUSDC quote works even before UpgradeCap is resolved.
     */
    hasDeepbookQuoteConfig() {
        return !!(this.constants.DEEPBOOK_UPGRADE_CAP_ID &&
            this.constants.DEEPBOOK_USDC_POOL_ID &&
            this.constants.DEEPBOOK_MARGIN_REGISTRY &&
            this.constants.DEEPBOOK_USDC_MARGIN_POOL_ID &&
            this.constants.DBUSDC_COIN_TYPE);
    }
    /**
     * True when the user is already paying with dbUSDC (same as market).
     * Then we only need to supply, no swap and no Deepbook deposit.
     */
    isDbUsdcInput(inputCoinType) {
        return (!!this.constants.DBUSDC_COIN_TYPE &&
            this.constants.DBUSDC_COIN_TYPE === inputCoinType);
    }
    /**
     * Zap-in supply when the target market is dbUSDC.
     * - If user pays dbUSDC: direct supply (no swap, no Deepbook deposit).
     * - If user pays USDC: deposit USDC to Deepbook pool → get dbUSDC → supply.
     * - If user pays another token: Cetus swap to USDC → Deepbook deposit → supply.
     */
    async zapInSupplyViaDeepbook(params) {
        const tx = params.tx ?? new Transaction();
        let coinToSupply;
        let usdcCoin;
        // Edge case: user already has dbUSDC — direct supply, no swap or Deepbook deposit.
        if (this.isDbUsdcInput(params.inputCoinType)) {
            coinToSupply = this.getCoinObject(tx, this.constants.DBUSDC_COIN_TYPE, params.address, BigInt(params.inputAmount));
        }
        else if (this.isUsdcInput(params.inputCoinType)) {
            // Path A: User pays USDC. Source USDC and use it for the Deepbook deposit.
            usdcCoin = this.getCoinObject(tx, this.constants.USDC_COIN_TYPE, params.address, BigInt(params.inputAmount));
        }
        else {
            // Path B: User pays another token. Swap to USDC via Cetus, then use that for Deepbook.
            const quoteResponse = await this.cetusSwap.getCetusSwapQuote(params.inputCoinType, this.constants.USDC_COIN_TYPE, params.inputAmount.toString());
            if (!quoteResponse) {
                console.error("Failed to get Cetus quote (input → USDC)");
                return undefined;
            }
            const inputCoin = this.getCoinObject(tx, params.inputCoinType, params.address, BigInt(quoteResponse.amountIn.toString()));
            const usdcFromSwap = await this.cetusSwap.cetusSwapTokensTxb(quoteResponse, params.slippage, inputCoin, params.address, tx);
            if (!usdcFromSwap) {
                console.error("Failed to get USDC from swap");
                return undefined;
            }
            usdcCoin = usdcFromSwap;
        }
        // Deepbook deposit only when we have USDC to deposit (Path A or B). Skip for Path 1c (direct dbUSDC).
        if (usdcCoin !== undefined) {
            coinToSupply = tx.moveCall({
                target: `${this.deepbookPackageId}::alphalend_deepbook_pool::deposit`,
                typeArguments: [this.constants.USDC_COIN_TYPE],
                arguments: [
                    tx.object(this.constants.DEEPBOOK_USDC_POOL_ID),
                    tx.object(this.constants.DEEPBOOK_USDC_MARGIN_POOL_ID),
                    tx.object(this.constants.DEEPBOOK_MARGIN_REGISTRY),
                    usdcCoin,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        // Supply dbUSDC to lending market (same as existing add_collateral flow).
        if (coinToSupply === undefined) {
            console.error("[zapInSupplyViaDeepbook] No coin to supply (internal error)");
            return undefined;
        }
        const marketIdU64 = typeof params.marketId === "string"
            ? Number(params.marketId)
            : params.marketId;
        if (params.positionCapId) {
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.marketCoinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    tx.object(params.positionCapId),
                    tx.pure.u64(marketIdU64),
                    coinToSupply,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        else {
            const positionCapId = await getUserPositionCapId(this.blockchain, params.address);
            let positionCap;
            if (positionCapId) {
                positionCap = tx.object(positionCapId);
            }
            else {
                positionCap = this.createPosition(tx);
            }
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.marketCoinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    positionCap,
                    tx.pure.u64(marketIdU64),
                    coinToSupply,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
            tx.transferObjects([positionCap], params.address);
        }
        return tx;
    }
    /**
     * Supplies collateral to the AlphaLend protocol with automatic token swapping
     *
     * This method performs a "zap in" operation by first swapping the input token
     * to the market's required collateral token via 7k Protocol, then supplying
     * the swapped tokens as collateral to the specified market.
     *
     * @param params Zap in supply parameters
     * @param params.marketId Market ID where collateral is being added
     * @param params.inputAmount Amount of input tokens to swap and supply (in base units)
     * @param params.inputCoinType Fully qualified type of the input token to swap from (e.g., "0x2::sui::SUI")
     * @param params.marketCoinType Fully qualified type of the market's collateral token to swap to
     * @param params.slippage Maximum allowed slippage percentage for the swap
     * @param params.positionCapId Optional: Object ID of the position capability object
     * @param params.address Address of the user performing the zap in supply
     * @returns Transaction object ready for signing and execution, or undefined if swap fails
     */
    async zapInSupply(params) {
        // Edge case: zero or invalid amount — reject for both paths.
        if (!params.inputAmount || params.inputAmount <= 0n) {
            console.error("[zapInSupply] inputAmount must be > 0");
            return undefined;
        }
        await this.ensureInitialized();
        // Step 1: When supplying to dbUSDC market, use Deepbook path (deposit USDC → dbUSDC) instead of Cetus.
        if (this.hasDeepbookConfig() &&
            this.isDbUsdcMarket(params.marketCoinType)) {
            return this.zapInSupplyViaDeepbook(params);
        }
        // Step 2: Default path — swap via Cetus, then supply.
        const tx = params.tx ?? new Transaction();
        const quoteResponse = await this.cetusSwap.getCetusSwapQuote(params.inputCoinType, params.marketCoinType, params.inputAmount.toString());
        if (!quoteResponse) {
            console.error("Failed to get swap quote");
            return undefined;
        }
        // Source the exact swap-input amount from address balance + coin objects.
        const inputCoin = this.getCoinObject(tx, params.inputCoinType, params.address, BigInt(quoteResponse.amountIn.toString()));
        const supplyCoin = await this.cetusSwap.cetusSwapTokensTxb(quoteResponse, params.slippage, inputCoin, params.address, tx);
        if (!supplyCoin) {
            console.error("Failed to get coin out from swap");
            return undefined;
        }
        if (params.positionCapId) {
            // Build add_collateral transaction
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.marketCoinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                    tx.object(params.positionCapId), // Position capability
                    tx.pure.u64(params.marketId), // Market ID
                    supplyCoin, // Coin to supply as collateral
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
                ],
            });
        }
        else {
            const positionCapId = await getUserPositionCapId(this.blockchain, params.address);
            let positionCap;
            if (positionCapId) {
                positionCap = tx.object(positionCapId);
            }
            else {
                positionCap = this.createPosition(tx);
            }
            // Build add_collateral transaction
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.marketCoinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                    positionCap, // Position capability
                    tx.pure.u64(params.marketId), // Market ID
                    supplyCoin, // Coin to supply as collateral
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
                ],
            });
            tx.transferObjects([positionCap], params.address);
        }
        return tx;
    }
    /**
     * Withdraws token collateral from the AlphaLend protocol
     *
     * @param params Withdraw parameters
     * @param params.marketId Market ID from which to withdraw
     * @param params.amount Amount to withdraw in base units (bigint, in mists, use MAX_U64 to withdraw all)
     * @param params.coinType Fully qualified coin type to withdraw (e.g., "0x2::sui::SUI")
     * @param params.positionCapId Object ID of the position capability object
     * @param params.address Address of the user withdrawing collateral
     * @param params.priceUpdateCoinTypes Array of coin types to update prices for
     * @returns Transaction object ready for signing and execution
     */
    async withdraw(params) {
        const tx = params.tx ?? new Transaction();
        // First update prices to ensure latest oracle values
        if (this.network === "mainnet") {
            await this.updatePrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes);
        }
        else {
            await setPrices(tx);
        }
        const promise = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::remove_collateral`,
            typeArguments: [params.coinType],
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                tx.object(params.positionCapId), // Position capability
                tx.pure.u64(params.marketId), // Market ID
                tx.pure.u64(params.amount), // Amount to withdraw
                tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
            ],
        });
        const isSui = params.coinType === this.constants.SUI_COIN_TYPE;
        let coin;
        if (isSui) {
            coin = tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::fulfill_promise_SUI`,
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    promise,
                    tx.object(this.constants.SUI_SYSTEM_STATE_ID),
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        else {
            coin = await this.handlePromise(tx, promise, params.coinType);
        }
        if (coin) {
            this.sendCoinToAddressBalance(tx, params.coinType, params.address, coin);
        }
        return tx;
    }
    /**
     * Gets a user's portfolio from a position capability ID
     *
     * @param positionCapId Position capability ID
     * @returns User portfolio, or undefined if position not found
     */
    async getUserPortfolioFromPositionCapId(positionCapId) {
        try {
            await this.ensureInitialized();
            const position = await this.lendingProtocol.getPositionFromPositionCapId(positionCapId);
            const markets = await this.lendingProtocol.getAllMarkets();
            return position.getUserPortfolio(markets);
        }
        catch (error) {
            console.error("Error getting position:", error);
            return undefined;
        }
    }
    /**
     * Withdraws collateral from the AlphaLend protocol with automatic token swapping
     *
     * This method performs a "zap out" operation by first withdrawing collateral
     * from the specified market, then swapping the withdrawn tokens to the desired
     * output token via 7k Protocol.
     *
     * @param params Zap out withdraw parameters
     * @param params.marketId Market ID from which collateral is being withdrawn
     * @param params.amount Amount to withdraw (in base units)
     * @param params.marketCoinType Fully qualified type of the market's collateral token to withdraw from
     * @param params.outputCoinType Fully qualified type of the desired output token to swap to (e.g., "0x2::sui::SUI")
     * @param params.slippage Maximum allowed slippage percentage for the swap
     * @param params.positionCapId Object ID of the position capability object
     * @param params.address Address of the user performing the zap out withdraw
     * @param params.priceUpdateCoinTypes Coin types of the coins whose price needs to be updated
     * @returns Transaction object ready for signing and execution, or undefined if swap fails
     */
    async zapOutWithdraw(params) {
        const tx = params.tx ?? new Transaction();
        let swapInAmount = (params.amount - 1n).toString();
        if (params.amount === MAX_U64) {
            // Refresh position to get latest collateral amount
            const position = await this.lendingProtocol.getPositionFromPositionCapId(params.positionCapId);
            const market = await this.lendingProtocol.getMarket(parseInt(params.marketId));
            position.refreshSingleMarket(market);
            const collateral = position.position.collaterals.find((collateral) => collateral.key === params.marketId);
            if (!collateral) {
                throw new Error("Collateral not found. User has not supplied to this market.");
            }
            swapInAmount = new Decimal(collateral.value)
                .mul(market.market.xtokenRatio)
                .div(1e18)
                .floor()
                .toString();
        }
        // First update prices to ensure latest oracle values
        if (this.network === "mainnet") {
            await this.updatePrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes);
        }
        else {
            await setPrices(tx);
        }
        const promise = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::remove_collateral`,
            typeArguments: [params.marketCoinType],
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                tx.object(params.positionCapId), // Position capability
                tx.pure.u64(params.marketId), // Market ID
                tx.pure.u64(params.amount), // Amount to withdraw
                tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
            ],
        });
        const isSui = params.marketCoinType === this.constants.SUI_COIN_TYPE;
        let coin;
        if (isSui) {
            coin = tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::fulfill_promise_SUI`,
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    promise,
                    tx.object(this.constants.SUI_SYSTEM_STATE_ID),
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        else {
            coin = await this.handlePromise(tx, promise, params.marketCoinType);
        }
        const quoteResponse = await this.cetusSwap.getCetusSwapQuote(params.marketCoinType, params.outputCoinType, swapInAmount);
        if (!quoteResponse) {
            throw new Error("Failed to get swap quote: Empty response from Cetus");
        }
        const withdrawCoin = await this.cetusSwap.routerSwapWithInputCoin(quoteResponse, tx, coin, params.slippage);
        if (withdrawCoin) {
            this.sendCoinToAddressBalance(tx, params.outputCoinType, params.address, withdrawCoin);
        }
        return tx;
    }
    async swapAndRepay(params) {
        const tx = params.tx ?? new Transaction();
        const swapInAmount = (params.amount - 1n).toString();
        // Get swap quote (no price update needed for swap itself)
        let quoteResponse;
        try {
            quoteResponse = await this.cetusSwap.getCetusSwapQuote(params.swapFromCoinType, params.swapToCoinType, swapInAmount);
        }
        catch (error) {
            console.error("[AlphaLend SDK] Error fetching Cetus quote:", error);
            throw new Error(`Failed to get swap quote: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
        if (!quoteResponse) {
            console.error("[AlphaLend SDK] Cetus returned empty quote");
            throw new Error("Failed to get swap quote: Empty response from Cetus");
        }
        // Source the exact swap-input amount from address balance + coin objects
        // (SUI handled inside getCoinObject via the gas coin).
        const inputCoin = this.getCoinObject(tx, params.swapFromCoinType, params.address, BigInt(quoteResponse.amountIn.toString()));
        // Perform the swap to get the coin for repayment
        let repayCoin;
        try {
            repayCoin = await this.cetusSwap.cetusSwapTokensTxb(quoteResponse, params.slippage, inputCoin, params.address, tx);
        }
        catch (error) {
            console.error("[AlphaLend SDK] Error building Cetus swap:", error);
            throw new Error(`Failed to build swap transaction: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
        if (!repayCoin) {
            console.error("[AlphaLend SDK] Cetus swap returned no coin");
            throw new Error("Failed to perform swap: No coin returned");
        }
        // Repay the debt with the swapped coin
        const remainingCoin = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::repay`,
            typeArguments: [params.swapToCoinType],
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                tx.object(params.positionCapId), // Position capability
                tx.pure.u64(params.marketId), // Market ID
                repayCoin, // Coin to repay with
                tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
            ],
        });
        // Credit remaining coin from repayment back to the user's address balance
        this.sendCoinToAddressBalance(tx, params.swapToCoinType, params.address, remainingCoin);
        return tx;
    }
    /**
     * Borrows tokens from the AlphaLend protocol
     *
     * @param params Borrow parameters
     * @param params.marketId Market ID to borrow from
     * @param params.amount Amount to borrow in base units (bigint, in mists)
     * @param params.coinType Fully qualified coin type to borrow (e.g., "0x2::sui::SUI")
     * @param params.positionCapId Object ID of the position capability object
     * @param params.address Address of the user borrowing tokens
     * @param params.priceUpdateCoinTypes Array of coin types to update prices for
     * @returns Transaction object ready for signing and execution
     */
    async borrow(params) {
        const tx = params.tx ?? new Transaction();
        // First update prices to ensure latest oracle values
        if (this.network === "mainnet") {
            await this.updatePrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes);
        }
        else {
            await setPrices(tx);
        }
        const promise = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::borrow`,
            typeArguments: [params.coinType],
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                tx.object(params.positionCapId), // Position capability
                tx.pure.u64(params.marketId), // Market ID
                tx.pure.u64(params.amount), // Amount to borrow
                tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
            ],
        });
        const isSui = params.coinType === this.constants.SUI_COIN_TYPE;
        let coin;
        if (isSui) {
            coin = tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::fulfill_promise_SUI`,
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    promise,
                    tx.object(this.constants.SUI_SYSTEM_STATE_ID),
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        else {
            coin = tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::fulfill_promise`,
                typeArguments: [params.coinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    promise,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        this.sendCoinToAddressBalance(tx, params.coinType, params.address, coin);
        return tx;
    }
    /**
     * Repays borrowed tokens to the AlphaLend protocol
     *
     * @param params Repay parameters
     * @param params.marketId Market ID where debt exists
     * @param params.amount Amount to repay in base units (bigint, in mists)
     * @param params.coinType Fully qualified coin type to repay (e.g., "0x2::sui::SUI")
     * @param params.positionCapId Object ID of the position capability object
     * @param params.address Address of the user repaying the debt
     * @returns Transaction object ready for signing and execution
     */
    async repay(params) {
        const tx = params.tx ?? new Transaction();
        // Source the repay coin from address balance + coin objects (SUI handled inside).
        const repayCoinA = this.getCoinObject(tx, params.coinType, params.address, BigInt(params.amount));
        // Build repay transaction
        const repayCoin = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::repay`,
            typeArguments: [params.coinType],
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                tx.object(params.positionCapId), // Position capability
                tx.pure.u64(params.marketId), // Market ID
                repayCoinA, // Coin to repay with
                tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
            ],
        });
        this.sendCoinToAddressBalance(tx, params.coinType, params.address, repayCoin);
        return tx;
    }
    /**
     * Claims rewards from the AlphaLend protocol
     *
     * @param params ClaimRewards parameters
     * @param params.positionCapId Object ID of the position capability object
     * @param params.address Address of the user claiming rewards
     * @deprecated Use claimAndDepositAlpha instead
     * @param params.claimAlpha Whether to claim and deposit Alpha token rewards
     * @deprecated Use claimAndDepositAll instead
     * @param params.claimAll Whether to claim and deposit all other reward tokens
     * @param params.claimAndDepositAlpha Whether to claim and deposit Alpha token rewards
     * @param params.claimAndDepositAll Whether to claim and deposit all other reward tokens
     *
     * On mainnet, SUI rewards are staked into stSUI via `liquid_staking::mint` and
     * the stSUI is transferred to the user (or supplied to the stSUI market when
     * `claimAndDepositAll` is set). Estimated SUI rewards below
     * `MIN_SUI_STAKE_AMOUNT` keep the plain SUI behavior.
     * @returns Transaction object ready for signing and execution
     */
    async claimRewards(params) {
        const tx = params.tx ?? new Transaction();
        params.claimAndDepositAlpha =
            params.claimAndDepositAlpha || params.claimAlpha;
        params.claimAndDepositAll = params.claimAndDepositAll || params.claimAll;
        const { rewardInput, claimableAmounts } = await getClaimRewardInput(this.blockchain, params.address, params.positionCapId);
        const convertSuiToStsui = this.network === "mainnet" &&
            (claimableAmounts.get(this.constants.SUI_COIN_TYPE) ?? 0n) >=
                MIN_SUI_STAKE_AMOUNT;
        let alphaCoin = undefined;
        let suiCoin = undefined;
        for (const data of rewardInput) {
            for (let coinType of data.coinTypes) {
                coinType = normalizeCoinType(coinType);
                const isSuiConversion = convertSuiToStsui && coinType === this.constants.SUI_COIN_TYPE;
                let coin1;
                let promise;
                if (params.claimAndDepositAll &&
                    coinType !== this.constants.ALPHA_COIN_TYPE &&
                    !isSuiConversion) {
                    [coin1, promise] = tx.moveCall({
                        target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::collect_reward_and_deposit`,
                        typeArguments: [coinType],
                        arguments: [
                            tx.object(this.constants.LENDING_PROTOCOL_ID),
                            tx.pure.u64(data.marketId),
                            tx.object(params.positionCapId),
                            tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                        ],
                    });
                }
                else {
                    [coin1, promise] = tx.moveCall({
                        target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::collect_reward`,
                        typeArguments: [coinType],
                        arguments: [
                            tx.object(this.constants.LENDING_PROTOCOL_ID),
                            tx.pure.u64(data.marketId),
                            tx.object(params.positionCapId),
                            tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                        ],
                    });
                }
                if (promise) {
                    const coin2 = await this.handlePromise(tx, promise, coinType);
                    if (params.claimAndDepositAlpha &&
                        coinType === this.constants.ALPHA_COIN_TYPE) {
                        if (coin2) {
                            alphaCoin = this.mergeCoins(tx, alphaCoin, [coin2]);
                        }
                        if (coin1) {
                            alphaCoin = this.mergeCoins(tx, alphaCoin, [coin1]);
                        }
                    }
                    else if (isSuiConversion) {
                        if (coin2) {
                            suiCoin = this.mergeCoins(tx, suiCoin, [coin2]);
                        }
                        if (coin1) {
                            suiCoin = this.mergeCoins(tx, suiCoin, [coin1]);
                        }
                    }
                    else {
                        if (coin2) {
                            this.sendCoinToAddressBalance(tx, coinType, params.address, coin2);
                        }
                        if (coin1) {
                            this.sendCoinToAddressBalance(tx, coinType, params.address, coin1);
                        }
                    }
                }
                else if (coin1) {
                    if (params.claimAndDepositAlpha &&
                        coinType === this.constants.ALPHA_COIN_TYPE) {
                        alphaCoin = this.mergeCoins(tx, alphaCoin, [coin1]);
                    }
                    else if (isSuiConversion) {
                        suiCoin = this.mergeCoins(tx, suiCoin, [coin1]);
                    }
                    else {
                        this.sendCoinToAddressBalance(tx, coinType, params.address, coin1);
                    }
                }
            }
        }
        if (suiCoin) {
            const stsuiCoin = this.mintStsui(tx, suiCoin);
            if (params.claimAndDepositAll) {
                tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                    typeArguments: [this.constants.STSUI_COIN_TYPE],
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        tx.object(params.positionCapId),
                        tx.pure.u64(this.constants.STSUI_MARKET_ID),
                        stsuiCoin,
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
            }
            else {
                this.sendCoinToAddressBalance(tx, this.constants.STSUI_COIN_TYPE, params.address, stsuiCoin);
            }
        }
        if (alphaCoin) {
            await this.depositAlphaTransaction(tx, alphaCoin, params.address);
        }
        return tx;
    }
    /**
     * Stakes a SUI coin into stSUI via `liquid_staking::mint`.
     * Aborts on-chain with EZeroLstMinted if the post-fee amount rounds to zero.
     */
    mintStsui(tx, suiCoin) {
        const [stsuiCoin] = tx.moveCall({
            target: `${this.constants.STSUI_LATEST_PACKAGE_ID}::liquid_staking::mint`,
            typeArguments: [this.constants.STSUI_COIN_TYPE],
            arguments: [
                tx.object(this.constants.LST_INFO),
                tx.object(this.constants.SUI_SYSTEM_STATE_ID),
                suiCoin,
            ],
        });
        return stsuiCoin;
    }
    /**
     * Merges multiple coins of the same type into a single coin
     *
     * @param tx Transaction to add merge operation to
     * @param targetCoin Existing coin to merge into (or undefined)
     * @param coins Array of coins to merge
     * @returns Transaction argument representing the merged coin
     */
    mergeCoins(tx, targetCoin, coins) {
        if (targetCoin) {
            tx.mergeCoins(targetCoin, coins);
        }
        else {
            targetCoin = tx.splitCoins(coins[0], [0]);
            tx.mergeCoins(targetCoin, coins);
        }
        return targetCoin;
    }
    /**
     * Claims all available rewards, swaps them into a single `targetCoinType`, then:
     * - transfers the swapped coin to the wallet if `transferTargetCoin` is true, otherwise
     * - repays `targetMarketId` if `isTargetBorrowed` is true (and transfers any remainder), otherwise
     * - supplies the swapped coin as collateral to `targetMarketId`.
     *
     * On mainnet, optionally updates prices before repay/supply if `priceUpdateCoinTypes` is provided.
     *
     * @param params ClaimSwapAndSupplyOrRepayOrTransferParams - swap/repay/supply configuration
     * @returns Transaction ready for signing and execution, or undefined if nothing is claimable
     */
    async claimSwapAndSupplyOrRepayOrTransfer(params) {
        const tx = params.tx ?? new Transaction();
        // ensureInitialized (coin metadata fetch) and getClaimRewardInput (position
        // + market reads) are independent network round-trips, so run them
        // concurrently instead of sequentially.
        const [, { rewardInput, claimableAmounts }] = await Promise.all([
            // Ensure SDK is initialized to have access to coin metadata (including prices)
            this.ensureInitialized(),
            // Get all claimable rewards
            getClaimRewardInput(this.blockchain, params.address, params.positionCapId),
        ]);
        if (!rewardInput || rewardInput.length === 0) {
            return undefined;
        }
        if (this.network === "mainnet" &&
            params.priceUpdateCoinTypes &&
            params.priceUpdateCoinTypes.length > 0) {
            await this.updatePrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes);
        }
        // Prefer the on-chain-derived claimable estimate over params.rewardAmounts:
        // callers pass display amounts (e.g. SUI rewards folded into stSUI), which
        // over-quote the swap and trip the aggregator's slippage check.
        const getRewardAmount = (coinType) => claimableAmounts.get(coinType)?.toString() ??
            params.rewardAmounts?.get(coinType);
        // Helper function to calculate USD value for a coin type
        const calculateUsdValue = (coinType) => {
            const rewardAmount = getRewardAmount(coinType);
            if (!rewardAmount)
                return 0;
            const coinMetadata = this.coinMetadataMap.get(coinType);
            // If metadata is missing, log warning and return null to indicate invalid calculation
            if (!coinMetadata || coinMetadata.decimals === undefined) {
                console.warn(`Missing coin metadata or decimals for ${coinType}.`);
                return null;
            }
            const decimals = coinMetadata.decimals;
            const tokenAmount = Number(rewardAmount) / Math.pow(10, decimals);
            const price = resolveCoinPrice(coinMetadata)?.toNumber() ?? 0;
            return tokenAmount * price;
        };
        // Pre-calculate USD values for all coin types
        const usdValuesByCoinType = new Map();
        for (const data of rewardInput) {
            for (let coinType of data.coinTypes) {
                coinType = normalizeCoinType(coinType);
                if (!usdValuesByCoinType.has(coinType)) {
                    const usdValue = calculateUsdValue(coinType);
                    usdValuesByCoinType.set(coinType, usdValue);
                }
            }
        }
        // Collect all reward coins by coin type
        const rewardCoinsByType = new Map();
        // Claim rewards (skip small rewards if claimSmallRewardsToWallet is false)
        for (const data of rewardInput) {
            for (let coinType of data.coinTypes) {
                coinType = normalizeCoinType(coinType);
                const usdValue = usdValuesByCoinType.get(coinType);
                // Skip coins with missing metadata (null usdValue)
                if (usdValue === null || usdValue === undefined) {
                    continue;
                }
                // Skip claiming small rewards if both checkboxes are checked (claimSmallRewardsToWallet = false)
                const shouldSkipSmallReward = !params.claimSmallRewardsToWallet && usdValue > 0 && usdValue < 0.01;
                if (shouldSkipSmallReward) {
                    continue; // Skip this reward entirely
                }
                const [coin1, promise] = tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::collect_reward`,
                    typeArguments: [coinType],
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        tx.pure.u64(data.marketId),
                        tx.object(params.positionCapId),
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
                if (promise) {
                    const coin2 = await this.handlePromise(tx, promise, coinType);
                    if (coin2) {
                        if (!rewardCoinsByType.has(coinType)) {
                            rewardCoinsByType.set(coinType, []);
                        }
                        rewardCoinsByType.get(coinType).push(coin2);
                    }
                }
                if (coin1) {
                    if (!rewardCoinsByType.has(coinType)) {
                        rewardCoinsByType.set(coinType, []);
                    }
                    rewardCoinsByType.get(coinType).push(coin1);
                }
            }
        }
        // Separate coins into those that should be swapped vs those that should be claimed directly
        const coinsToSwap = new Map();
        const coinsToClaimDirectly = new Map();
        for (const [coinType, coins] of rewardCoinsByType.entries()) {
            if (coins.length === 0)
                continue;
            const mergedCoin = this.mergeCoins(tx, undefined, coins);
            const usdValue = usdValuesByCoinType.get(coinType);
            // Skip coins with missing metadata (should not happen as they're filtered earlier, but safety check)
            if (usdValue === null || usdValue === undefined) {
                console.warn(`[claimSwapAndSupplyOrRepayOrTransfer] Skipping coin ${coinType} - missing USD value calculation`);
                continue;
            }
            // Check if this coin is above the $0.01 threshold
            const shouldSwap = usdValue >= 0.01;
            if (shouldSwap) {
                coinsToSwap.set(coinType, mergedCoin);
            }
            else {
                // Small rewards to be claimed directly to wallet
                coinsToClaimDirectly.set(coinType, mergedCoin);
            }
        }
        // Swap eligible coins to target coin
        let targetCoin = undefined;
        for (const [coinType, mergedCoin] of coinsToSwap.entries()) {
            if (coinType === params.targetCoinType) {
                if (targetCoin) {
                    tx.mergeCoins(targetCoin, [mergedCoin]);
                }
                else {
                    targetCoin = mergedCoin;
                }
            }
            else {
                // Use estimated claimable amount for quote, or fallback to 1 token
                const quoteAmount = getRewardAmount(coinType) || "1000000000";
                const router = await this.cetusSwap.getCetusSwapQuote(coinType, params.targetCoinType, quoteAmount);
                const swappedCoin = await this.cetusSwap.routerSwapWithInputCoin(router, tx, mergedCoin, params.slippage);
                if (targetCoin) {
                    tx.mergeCoins(targetCoin, [swappedCoin]);
                }
                else {
                    targetCoin = swappedCoin;
                }
            }
        }
        // Handle coins that should be claimed directly to the user's address balance
        if (coinsToClaimDirectly.size > 0) {
            for (const [coinType, coin] of coinsToClaimDirectly.entries()) {
                this.sendCoinToAddressBalance(tx, coinType, params.address, coin);
            }
        }
        // If no target coin, all rewards were either too small or claimed directly
        if (!targetCoin) {
            // If we have coins claimed directly, return the transaction
            if (coinsToClaimDirectly.size > 0) {
                return tx;
            }
            // Otherwise, nothing to do
            console.error("No target coin available and no small rewards to claim");
            return undefined;
        }
        if (params.transferTargetCoin) {
            this.sendCoinToAddressBalance(tx, params.targetCoinType, params.address, targetCoin);
            return tx;
        }
        if (params.isTargetBorrowed) {
            // Repay the debt using the entire targetCoin
            const remainingCoin = tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::repay`,
                typeArguments: [params.targetCoinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    tx.object(params.positionCapId),
                    tx.pure.u64(params.targetMarketId || 0),
                    targetCoin,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
            // todo - check if remainingCoin is zero and destroy it if it is
            this.sendCoinToAddressBalance(tx, params.targetCoinType, params.address, remainingCoin);
        }
        else {
            // Supply the remaining reward coin to the target market
            tx.moveCall({
                target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                typeArguments: [params.targetCoinType],
                arguments: [
                    tx.object(this.constants.LENDING_PROTOCOL_ID),
                    tx.object(params.positionCapId),
                    tx.pure.u64(params.targetMarketId || 0),
                    targetCoin,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        return tx;
    }
    /**
     * Claims all available rewards, then repays debts for reward coin types present in `borrowedCoins`.
     * Any remainder returned by `repay` is transferred to the wallet.
     *
     * Note: reward coin types that are not present in `borrowedCoins` are currently not processed.
     *
     * On mainnet, SUI rewards are staked into stSUI via `liquid_staking::mint` and
     * processed as stSUI, so `borrowedCoins`/`supplyMarkets` lookups run against the
     * stSUI coin type. Estimated SUI rewards below `MIN_SUI_STAKE_AMOUNT` keep
     * the plain SUI behavior.
     *
     * On mainnet, optionally updates prices first if `priceUpdateCoinTypes` is provided.
     *
     * @param params ClaimAndSupplyOrRepayParams - claim + repay configuration
     * @returns Transaction object ready for signing and execution
     */
    async claimAndSupplyOrRepay(params) {
        const tx = params.tx ?? new Transaction();
        const shouldUpdatePrices = this.network === "mainnet" &&
            !!params.priceUpdateCoinTypes &&
            params.priceUpdateCoinTypes.length > 0;
        // updatePrices (Pyth fetch + price-update moveCalls) and getClaimRewardInput
        // (position + market reads) are independent network round-trips, so run them
        // concurrently. updatePrices still appends its price-update calls before the
        // reward-collection loop below, preserving transaction ordering.
        const [, { rewardInput, claimableAmounts }] = await Promise.all([
            shouldUpdatePrices
                ? this.updatePrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes)
                : Promise.resolve(),
            getClaimRewardInput(this.blockchain, params.address, params.positionCapId),
        ]);
        const convertSuiToStsui = this.network === "mainnet" &&
            (claimableAmounts.get(this.constants.SUI_COIN_TYPE) ?? 0n) >=
                MIN_SUI_STAKE_AMOUNT;
        // Collect rewards for each market and coin type
        const rewardCoinsByType = new Map();
        const addCoinToMap = (coinType, coin) => {
            const coins = rewardCoinsByType.get(coinType);
            if (coins) {
                coins.push(coin);
            }
            else {
                rewardCoinsByType.set(coinType, [coin]);
            }
        };
        const suiCoins = [];
        for (const data of rewardInput) {
            for (let coinType of data.coinTypes) {
                coinType = normalizeCoinType(coinType);
                // SUI destined for stSUI must be collected without the on-chain
                // auto-deposit so the coin is available to stake.
                const isSuiConversion = convertSuiToStsui && coinType === this.constants.SUI_COIN_TYPE;
                // Collect reward for this coin type
                const [coin1, promise] = tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::${isSuiConversion ? "collect_reward" : "collect_reward_and_deposit"}`,
                    typeArguments: [coinType],
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        tx.pure.u64(data.marketId),
                        tx.object(params.positionCapId),
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
                // Handle promise and add coins to map
                if (promise) {
                    const coin2 = await this.handlePromise(tx, promise, coinType);
                    if (coin2) {
                        if (isSuiConversion)
                            suiCoins.push(coin2);
                        else
                            addCoinToMap(coinType, coin2);
                    }
                }
                if (coin1) {
                    if (isSuiConversion)
                        suiCoins.push(coin1);
                    else
                        addCoinToMap(coinType, coin1);
                }
            }
        }
        // Stake claimed SUI into stSUI and process it under the stSUI coin type
        const stsuiCoinType = normalizeCoinType(this.constants.STSUI_COIN_TYPE);
        if (suiCoins.length > 0) {
            const mergedSui = this.mergeCoins(tx, undefined, suiCoins);
            const stsuiCoin = this.mintStsui(tx, mergedSui);
            addCoinToMap(stsuiCoinType, stsuiCoin);
        }
        // Function to supply coin to market or transfer to wallet
        const handleCoin = (coin, coinType, supplyInfo) => {
            if (supplyInfo) {
                tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::add_collateral`,
                    typeArguments: [coinType],
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        tx.object(params.positionCapId),
                        tx.pure.u64(supplyInfo.marketId),
                        coin,
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
            }
            else {
                this.sendCoinToAddressBalance(tx, coinType, params.address, coin);
            }
        };
        // Process each reward coin type
        for (const [coinType, coins] of rewardCoinsByType.entries()) {
            if (coins.length === 0)
                continue;
            // Merge all coins of this type
            const mergedCoin = tx.splitCoins(coins[0], [0])[0];
            tx.mergeCoins(mergedCoin, coins);
            const borrowInfo = params.borrowedCoins.get(coinType);
            if (borrowInfo) {
                // Repay the debt and handle leftover
                const remainingCoin = tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::repay`,
                    typeArguments: [coinType],
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        tx.object(params.positionCapId),
                        tx.pure.u64(borrowInfo.marketId),
                        mergedCoin,
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
                // todo - check if remainingCoin is zero and destroy it if it is
                this.sendCoinToAddressBalance(tx, coinType, params.address, remainingCoin);
            }
            else {
                // Not borrowed - supply or transfer. stSUI minted from SUI rewards has
                // no caller-provided supply market; default to the stSUI market so it
                // is supplied like other rewards (mirrors collect_reward_and_deposit's
                // on-chain auto-deposit).
                let supplyInfo = params.supplyMarkets?.get(coinType);
                if (!supplyInfo && suiCoins.length > 0 && coinType === stsuiCoinType) {
                    supplyInfo = { marketId: String(this.constants.STSUI_MARKET_ID) };
                }
                handleCoin(mergedCoin, coinType, supplyInfo);
            }
        }
        return tx;
    }
    /**
     * Liquidates an unhealthy position
     *
     * @param params Liquidate parameters - liquidatePositionId, borrowMarketId, withdrawMarketId, repayAmount,
     *               borrowCoinType, withdrawCoinType, coinObjectId, priceUpdateCoinTypes
     * @returns Transaction object ready for signing and execution
     */
    async liquidate(params) {
        const tx = params.tx ?? new Transaction();
        // First update prices to ensure latest oracle values
        if (this.network === "mainnet") {
            if (params.updateAllPrices) {
                await this.updateAllPrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes);
            }
            else {
                await this.updatePrices(tx, params.priceUpdateCoinTypes, params.lazerUpdateBytes);
            }
        }
        else {
            await setPrices(tx);
        }
        // Build liquidate transaction
        const [promise, coin1] = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::liquidate`,
            typeArguments: [params.borrowCoinType, params.withdrawCoinType],
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
                tx.pure.id(params.liquidatePositionId), // Position ID to liquidate
                tx.pure.u64(params.borrowMarketId), // Borrow market ID
                tx.pure.u64(params.withdrawMarketId), // Withdraw market ID
                params.repayCoin, // Coin to repay with
                tx.object(this.constants.SUI_CLOCK_OBJECT_ID), // Clock object
            ],
        });
        let coin2;
        if (promise) {
            coin2 = await this.handlePromise(tx, promise, params.withdrawCoinType);
        }
        return [coin1, coin2];
    }
    /**
     * Breaks out of a leveraged looping position using a Navi flash loan.
     * Use like withdraw/repay: same client method + params pattern; returns Transaction for signing.
     *
     * @param params FlashRepayParams - flash repay parameters
     * @returns Transaction ready for signing and execution
     */
    async flashRepay(params) {
        return await buildFlashRepayTransaction(this, params);
    }
    /**
     * Creates a new position in the protocol
     *
     * @returns Transaction object for creating a new position
     */
    createPosition(tx) {
        const positionCap = tx.moveCall({
            target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::create_position`,
            arguments: [
                tx.object(this.constants.LENDING_PROTOCOL_ID), // Protocol object
            ],
        });
        return positionCap;
    }
    // Query methods for interacting with on-chain data
    /**
     * Gets statistics of the protocol
     *
     * @returns Promise resolving to a ProtocolStats object
     */
    async getProtocolStats() {
        try {
            await this.ensureInitialized();
            const markets = await this.lendingProtocol.getAllMarkets();
            const stats = await this.lendingProtocol.getProtocolStats(markets);
            return stats;
        }
        catch (error) {
            console.error("Error getting protocol stats:", error);
            return undefined;
        }
    }
    /**
     * Gets statistics of the protocol with cached markets data
     *
     * @returns Promise resolving to a ProtocolStats object
     */
    async getProtocolStatsWithCachedMarkets(markets) {
        try {
            await this.ensureInitialized();
            const stats = await this.lendingProtocol.getProtocolStats(markets);
            return stats;
        }
        catch (error) {
            console.error("Error getting protocol stats:", error);
            return undefined;
        }
    }
    /**
     * Gets all markets data from the protocol
     *
     * @param options - Optional configuration for caching
     * @param options.useCache - Whether to use blockchain cache (default: false)
     * @param options.cacheTTL - Custom cache TTL in milliseconds (default: 60000)
     * @returns Promise resolving to an array of Market objects
     */
    async getAllMarkets(options) {
        try {
            await this.ensureInitialized();
            const useCache = options?.useCache ?? false;
            const cacheTTL = options?.cacheTTL;
            return await blockchainCache.getOrFetch("markets:all", async () => {
                const markets = await this.lendingProtocol.getAllMarketsData();
                return markets;
            }, {
                skipCache: !useCache,
                ttl: cacheTTL,
            });
        }
        catch (error) {
            console.error("Error getting markets:", error);
            return undefined;
        }
    }
    /**
     * Gets market data of a particular market id from the protocol
     *
     * @returns Promise resolving to a MarketData object
     */
    async getMarketDataFromId(marketId) {
        try {
            await this.ensureInitialized();
            const market = await this.lendingProtocol.getMarketData(marketId);
            return market;
        }
        catch (error) {
            console.error("Error getting market:", error);
            return undefined;
        }
    }
    /**
     * Gets all markets data from the protocol with cached markets chain data
     *
     * @returns Promise resolving to an array of MarketData objects
     */
    async getAllMarketsWithCachedMarkets(markets) {
        try {
            await this.ensureInitialized();
            return await Promise.all(markets.map((market) => market.getMarketData()));
        }
        catch (error) {
            console.error("Error getting markets:", error);
            return undefined;
        }
    }
    /**
     * Gets all markets chain data to cache
     *
     * @returns Promise resolving to an array of Market objects
     */
    async getMarketsChain() {
        try {
            await this.ensureInitialized();
            const markets = await this.lendingProtocol.getAllMarkets();
            return markets;
        }
        catch (error) {
            console.error("Error getting markets:", error);
            return undefined;
        }
    }
    /**
     * Gets user portfolio data
     *
     * @param userAddress The user's address for which to fetch portfolio data
     * @returns Promise resolving to an array of UserPortfolio objects or undefined if not found
     */
    async getUserPortfolio(userAddress) {
        try {
            await this.ensureInitialized();
            const portfolio = await this.lendingProtocol.getUserPortfolio(userAddress);
            return portfolio;
        }
        catch (error) {
            console.error("Error getting portfolio:", error);
            return undefined;
        }
    }
    /**
     * Gets portfolio data from position id with cached markets data
     *
     * @param positionId The position id for which to fetch portfolio data
     * @param markets The cached markets data to use for the portfolio
     * @returns Promise resolving to a UserPortfolio object or undefined if not found
     */
    async getUserPortfolioFromPositionWithCachedMarkets(positionId, markets) {
        try {
            await this.ensureInitialized();
            const position = await this.lendingProtocol.getPosition(positionId);
            return position.getUserPortfolio(markets);
        }
        catch (error) {
            console.error("Error getting portfolio:", error);
            return undefined;
        }
    }
    /**
     * Gets user portfolio data with cached markets data
     *
     * @param userAddress The user's address for which to fetch portfolio data
     * @param markets The cached markets data to use for the portfolio
     * @returns Promise resolving to an array of UserPortfolio objects or undefined if not found
     */
    async getUserPortfolioWithCachedMarkets(userAddress, markets) {
        try {
            await this.ensureInitialized();
            const portfolio = await this.lendingProtocol.getUserPortfolioWithMarkets(userAddress, markets);
            return portfolio;
        }
        catch (error) {
            console.error("Error getting portfolio:", error);
            return undefined;
        }
    }
    /**
     * Gets user portfolio data for a specific position
     *
     * @param positionId The position ID to get portfolio data for
     * @returns Promise resolving to a UserPortfolio object or undefined if not found
     */
    async getUserPortfolioFromPosition(positionId) {
        try {
            await this.ensureInitialized();
            const position = await this.lendingProtocol.getPosition(positionId);
            const markets = await this.lendingProtocol.getAllMarkets();
            return position.getUserPortfolio(markets);
        }
        catch (error) {
            console.error("Error getting position:", error);
            return undefined;
        }
    }
    /**
     * Gets a coin object suitable for a transaction. Upto 200 coins are merged together and returned.
     *
     * @param tx Transaction to which the coin will be added
     * @param type Fully qualified coin type to source
     * @param address Owner address whose balance/coins are spent (sets tx sender)
     * @param amount Exact coin amount in base units
     * @returns Transaction argument representing the exact-amount coin
     */
    getCoinObject(tx, type, address, amount) {
        return this.blockchain.getCoinObject(tx, type, address, amount);
    }
    /**
     * Credits a coin to `address`'s address balance (the accumulator) instead of
     * sending it as a standalone coin object. Used to return withdrawn/borrowed
     * coins and leftovers back to the user.
     *
     * @param tx Transaction to which the move call will be added
     * @param type Fully qualified coin type being returned
     * @param address Recipient whose address balance is credited
     * @param coin The coin to credit (consumed by the call)
     */
    sendCoinToAddressBalance(tx, type, address, coin) {
        this.blockchain.sendCoinToAddressBalance(tx, type, address, coin);
    }
    async handlePromise(tx, promise, coinType) {
        if (promise) {
            if (coinType === this.constants.SUI_COIN_TYPE ||
                coinType === this.constants.SUI_COIN_TYPE_LONG) {
                const coin = tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::fulfill_promise_SUI`,
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        promise,
                        tx.object(this.constants.SUI_SYSTEM_STATE_ID),
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
                return coin;
            }
            else {
                const coin = tx.moveCall({
                    target: `${this.constants.ALPHALEND_LATEST_PACKAGE_ID}::alpha_lending::fulfill_promise`,
                    typeArguments: [coinType],
                    arguments: [
                        tx.object(this.constants.LENDING_PROTOCOL_ID),
                        promise,
                        tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                    ],
                });
                return coin;
            }
        }
        return undefined;
    }
    async depositAlphaTransaction(tx, supplyCoin, address) {
        const constants = getAlphafiConstants();
        const receipt = await getAlphaReceipt(this.blockchain, address);
        if (receipt.length === 0) {
            const noneReceipt = tx.moveCall({
                target: `0x1::option::none`,
                typeArguments: [constants.ALPHA_POOL_RECEIPT],
                arguments: [],
            });
            tx.moveCall({
                target: `${constants.ALPHA_LATEST_PACKAGE_ID}::alphapool::user_deposit`,
                typeArguments: [constants.ALPHA_COIN_TYPE],
                arguments: [
                    tx.object(constants.VERSION),
                    noneReceipt,
                    tx.object(constants.ALPHA_POOL),
                    tx.object(constants.ALPHA_DISTRIBUTOR),
                    supplyCoin,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
        else {
            const someReceipt = tx.moveCall({
                target: `0x1::option::some`,
                typeArguments: [constants.ALPHA_POOL_RECEIPT],
                arguments: [tx.object(receipt[0].objectId)],
            });
            tx.moveCall({
                target: `${constants.ALPHA_LATEST_PACKAGE_ID}::alphapool::user_deposit`,
                typeArguments: [constants.ALPHA_COIN_TYPE],
                arguments: [
                    tx.object(constants.VERSION),
                    someReceipt,
                    tx.object(constants.ALPHA_POOL),
                    tx.object(constants.ALPHA_DISTRIBUTOR),
                    supplyCoin,
                    tx.object(this.constants.SUI_CLOCK_OBJECT_ID),
                ],
            });
        }
    }
    async getEstimatedGasBudget(tx, address) {
        return this.blockchain.getEstimatedGasBudget(tx, address);
    }
    async getSwapQuote(tokenIn, tokenOut, amountIn) {
        await this.ensureInitialized();
        const amountInBigInt = BigInt(amountIn);
        const coinIn = this.coinMetadataMap.get(tokenIn);
        const coinOut = this.coinMetadataMap.get(tokenOut);
        // When receive is dbUSDC: show swap quote tokenIn → USDC only (no pool rate; UI shows USDC-out then deposit to dbUSDC).
        // Use hasDeepbookQuoteConfig (static IDs only) so quote works even before UpgradeCap is resolved.
        if (this.hasDeepbookQuoteConfig() &&
            tokenOut === this.constants.DBUSDC_COIN_TYPE) {
            let estimatedAmountOut;
            let rawQuote = null;
            if (tokenIn === this.constants.DBUSDC_COIN_TYPE ||
                tokenIn === this.constants.USDC_COIN_TYPE) {
                estimatedAmountOut = amountInBigInt;
            }
            else {
                const quoteResponse = await this.cetusSwap.getCetusSwapQuote(tokenIn, this.constants.USDC_COIN_TYPE, amountIn);
                if (!quoteResponse)
                    return null;
                rawQuote = quoteResponse;
                estimatedAmountOut = BigInt(quoteResponse.amountOut.toString());
            }
            // Use USDC for output USD/slippage: estimatedAmountOut is USDC from Cetus, so price must match.
            const coinForOutputUSD = this.coinMetadataMap.get(this.constants.USDC_COIN_TYPE);
            let inputAmountInUSD = 0;
            let outputAmountInUSD = 0;
            let slippage = 0;
            // A pegged 0 stays falsy here, so the USD/slippage estimate is skipped
            // rather than computed from a substituted market quote.
            const priceA = resolveCoinPrice(coinIn)?.toNumber();
            const priceB = resolveCoinPrice(coinForOutputUSD)?.toNumber();
            if (priceA &&
                priceB &&
                coinIn?.decimals != null &&
                coinForOutputUSD?.decimals != null) {
                inputAmountInUSD =
                    (Number(amountInBigInt) / Math.pow(10, coinIn.decimals)) * priceA;
                outputAmountInUSD =
                    (Number(estimatedAmountOut) /
                        Math.pow(10, coinForOutputUSD.decimals)) *
                        priceB;
                if (inputAmountInUSD > 0)
                    slippage = (inputAmountInUSD - outputAmountInUSD) / inputAmountInUSD;
            }
            return {
                gateway: "cetus",
                estimatedAmountOut,
                estimatedFeeAmount: 0n,
                inputAmount: amountInBigInt,
                inputAmountInUSD,
                estimatedAmountOutInUSD: outputAmountInUSD,
                slippage,
                rawQuote,
            };
        }
        const quoteResponse = await this.cetusSwap.getCetusSwapQuote(tokenIn, tokenOut, amountIn);
        if (!quoteResponse)
            return null;
        // const sevenKEstimatedAmountOut = BigInt(
        //   quoteResponse ? quoteResponse.returnAmountWithDecimal.toString() : 0,
        // );
        // const sevenKEstimatedAmountOutWithoutFee = BigInt(
        //   quoteResponse
        //     ? quoteResponse.returnAmountWithoutSwapFees
        //       ? quoteResponse.returnAmountWithoutSwapFees.toString()
        //       : sevenKEstimatedAmountOut.toString()
        //     : sevenKEstimatedAmountOut.toString(),
        // );
        // const sevenKEstimatedFeeAmount =
        //   sevenKEstimatedAmountOut - sevenKEstimatedAmountOutWithoutFee;
        // const amount = BigInt(
        //   quoteResponse ? quoteResponse.swapAmountWithDecimal : 0,
        // );
        // let quote: quoteObject;
        // const priceA = coinIn?.pythPrice || coinIn?.coingeckoPrice;
        // const priceB = coinOut?.pythPrice || coinOut?.coingeckoPrice;
        // const coinAExpo = coinIn?.decimals;
        // const coinBExpo = coinOut?.decimals;
        // if (priceA && priceB && coinAExpo && coinBExpo) {
        //   const inputAmountInUSD =
        //     (Number(amount) / Math.pow(10, coinAExpo)) * parseFloat(priceA);
        //   const outputAmountInUSD =
        //     (Number(sevenKEstimatedAmountOut) / Math.pow(10, coinBExpo)) *
        //     parseFloat(priceB);
        //   const slippage =
        //     (inputAmountInUSD - outputAmountInUSD) / inputAmountInUSD;
        //   quote = {
        //     gateway: "7k",
        //     estimatedAmountOut: sevenKEstimatedAmountOut,
        //     estimatedFeeAmount: sevenKEstimatedFeeAmount,
        //     inputAmount: amount,
        //     inputAmountInUSD: inputAmountInUSD,
        //     estimatedAmountOutInUSD: outputAmountInUSD,
        //     slippage: slippage,
        //     rawQuote: quoteResponse,
        //   };
        // } else {
        //   console.warn(
        //     "Could not get prices from Pyth Network, using fallback pricing.",
        //   );
        //   quote = {
        //     gateway: "7k",
        //     estimatedAmountOut: sevenKEstimatedAmountOut,
        //     estimatedFeeAmount: sevenKEstimatedFeeAmount,
        //     inputAmount: amount,
        //     inputAmountInUSD: 0,
        //     estimatedAmountOutInUSD: 0,
        //     slippage: 0,
        //     rawQuote: quoteResponse,
        //   };
        // }
        const cetusEstimatedAmountOut = BigInt(quoteResponse ? quoteResponse.amountOut.toString() : 0);
        // const sevenKEstimatedAmountOutWithoutFee = BigInt(
        //   quoteResponse
        //     ? quoteResponse.amountOut.toString()
        //       ? quoteResponse.amountOut.toString()
        //       : cetusEstimatedAmountOut.toString()
        //     : cetusEstimatedAmountOut.toString(),
        // );
        // const cetusEstimatedFeeAmount =
        //   sevenKEstimatedAmountOut - sevenKEstimatedAmountOutWithoutFee;
        const amount = BigInt(quoteResponse ? quoteResponse.amountIn.toString() : 0);
        let quote;
        // A pegged 0 stays falsy here, so the USD/slippage estimate is skipped
        // rather than computed from a substituted market quote.
        const priceA = resolveCoinPrice(coinIn)?.toNumber();
        const priceB = resolveCoinPrice(coinOut)?.toNumber();
        const coinAExpo = coinIn?.decimals;
        const coinBExpo = coinOut?.decimals;
        if (priceA && priceB && coinAExpo && coinBExpo) {
            const inputAmountInUSD = (Number(amount) / Math.pow(10, coinAExpo)) * priceA;
            const outputAmountInUSD = (Number(cetusEstimatedAmountOut) / Math.pow(10, coinBExpo)) * priceB;
            const slippage = (inputAmountInUSD - outputAmountInUSD) / inputAmountInUSD;
            quote = {
                gateway: "cetus",
                estimatedAmountOut: cetusEstimatedAmountOut,
                estimatedFeeAmount: 0n,
                inputAmount: amount,
                inputAmountInUSD: inputAmountInUSD,
                estimatedAmountOutInUSD: outputAmountInUSD,
                slippage: slippage,
                rawQuote: quoteResponse,
            };
        }
        else {
            console.warn("Could not get prices from Pyth Network, using fallback pricing.");
            quote = {
                gateway: "cetus",
                estimatedAmountOut: cetusEstimatedAmountOut,
                estimatedFeeAmount: 0n,
                inputAmount: amount,
                inputAmountInUSD: 0,
                estimatedAmountOutInUSD: 0,
                slippage: 0,
                rawQuote: quoteResponse,
            };
        }
        return quote;
    }
    async getSwapTransactionBlock(tx, address, slippage, quoteResponse, coinIn) {
        await this.ensureInitialized();
        return await this.cetusSwap.cetusSwapTokensTxb(quoteResponse, slippage, coinIn, address, tx);
    }
    /**
     * Ensures the client is fully initialized before any operation:
     * 1. Resolves the live deepbook package ID from the UpgradeCap (once, for dbUSDC operations).
     * 2. Fetches and caches coin metadata from the GraphQL API (once, for all other operations).
     * Both steps are skipped on subsequent calls — zero extra RPC after first initialization.
     */
    async ensureInitialized() {
        // Resolve deepbook package ID exactly once, independent of isInitialized.
        // This ensures it runs even when coinMetadataMap is pre-supplied via constructor options.
        if (!this.deepbookPackageIdResolved) {
            if (!this.deepbookPackageIdPromise) {
                this.deepbookPackageIdPromise = this.resolveLatestDeepbookPackageId()
                    .catch((err) => {
                    console.warn(`[AlphalendClient] deepbook package ID resolution failed: ${err}`);
                })
                    .finally(() => {
                    this.deepbookPackageIdResolved = true;
                });
            }
            await this.deepbookPackageIdPromise;
        }
        if (this.isInitialized) {
            return;
        }
        if (this.initializationPromise) {
            return this.initializationPromise;
        }
        this.initializationPromise = this.fetchAndCacheCoinMetadata();
        return this.initializationPromise;
    }
    /**
     * Reads the alphalend_deepbook UpgradeCap from chain and sets deepbookPackageId
     * to whatever is currently live on-chain.
     *
     * Sui's upgrade mechanism updates the UpgradeCap's `package` field to the latest
     * published-at address on every upgrade. The UpgradeCap object ID never changes.
     */
    async resolveLatestDeepbookPackageId() {
        const capId = this.constants.DEEPBOOK_UPGRADE_CAP_ID;
        if (!capId)
            return; // dev/test env has empty string — skip
        try {
            const obj = await this.blockchain.getObject(capId);
            const latestPkgId = obj?.contents?.package;
            if (latestPkgId) {
                this.deepbookPackageId = latestPkgId;
            }
        }
        catch (err) {
            console.warn(`[AlphalendClient] Could not resolve deepbook package ID from UpgradeCap (${capId}). Error: ${err}`);
        }
    }
    /**
     * Fetches coin metadata from the AlphaLend GraphQL API and caches it.
     */
    async fetchAndCacheCoinMetadata() {
        try {
            const apiUrl = "https://api.alphalend.xyz/public/graphql";
            // Extended query to get all the data we need
            const query = `
        query {
          coinInfo {
            coinType
            pythPriceFeedId
            pythPriceInfoObjectId
            decimals
            pythSponsored
            symbol
            coingeckoPrice
            pythPrice
          }
        }
      `;
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ query }),
            });
            if (!response.ok) {
                throw new Error(`GraphQL request failed: ${response.status}`);
            }
            const result = await response.json();
            const coinInfoArray = result.data.coinInfo;
            // Cache the market data
            for (const coin of coinInfoArray) {
                if (coin.coinType &&
                    coin.pythSponsored !== null &&
                    coin.decimals &&
                    coin.symbol) {
                    this.coinMetadataMap.set(coin.coinType, {
                        coinType: coin.coinType,
                        pythPriceFeedId: coin.pythPriceFeedId,
                        pythPriceInfoObjectId: coin.pythPriceInfoObjectId,
                        decimals: coin.decimals,
                        pythSponsored: coin.pythSponsored,
                        symbol: coin.symbol,
                        coingeckoPrice: coin.coingeckoPrice,
                        pythPrice: coin.pythPrice,
                    });
                }
            }
            const alphaCoinType = "0xfe3afec26c59e874f3c1d60b8203cb3852d2bb2aa415df9548b8d688e6683f93::alpha::ALPHA";
            const alphaCoin = this.coinMetadataMap.get(alphaCoinType);
            if (alphaCoin) {
                this.coinMetadataMap.set(alphaCoinType, {
                    ...alphaCoin,
                    pythPriceFeedId: "93da3352f9f1d105fdfe4971cfa80e9dd777bfc5d0f683ebb6e1294b92137bb7",
                    pythPriceInfoObjectId: "0x29e37978cb1c9501bda5d7c105f24f0058bc1668637e307fbc290dba48cb918d",
                    pythSponsored: false,
                    pythPrice: alphaCoin.coingeckoPrice,
                });
            }
            const longSuiCoinType = "0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI";
            const suiCoinMetadata = this.coinMetadataMap.get("0x2::sui::SUI");
            if (suiCoinMetadata) {
                this.coinMetadataMap.set(longSuiCoinType, suiCoinMetadata);
            }
            this.isInitialized = true;
            // Update LendingProtocol with the fetched coin metadata
            this.lendingProtocol.updateCoinMetadataMap(this.coinMetadataMap);
        }
        catch (error) {
            throw new Error(`Failed to initialize market data: ${error instanceof Error ? error.message : "Unknown error"}. The SDK requires market data to function properly.`);
        }
    }
    /**
     * Gets Pyth price feed ID for a coin type
     * Uses dynamic data fetched from GraphQL API
     */
    getPythPriceFeedId(coinType) {
        const dynamicData = this.coinMetadataMap.get(coinType);
        if (dynamicData?.pythPriceFeedId) {
            return dynamicData.pythPriceFeedId;
        }
        throw new Error(`No Pyth price feed ID found for coin type: ${coinType}. Ensure the coin metadata is properly initialized.`);
    }
    /**
     * Gets price info object ID for a coin type
     * Uses dynamic data fetched from GraphQL API
     */
    getPythPriceInfoObjectId(coinType) {
        const dynamicData = this.coinMetadataMap.get(coinType);
        if (dynamicData?.pythPriceInfoObjectId) {
            return dynamicData.pythPriceInfoObjectId;
        }
        throw new Error(`No price info object ID found for coin type: ${coinType}. Ensure the coin metadata is properly initialized.`);
    }
    /**
     * Gets decimal places for a coin type
     * Uses dynamic data fetched from GraphQL API
     */
    getDecimals(coinType) {
        const dynamicData = this.coinMetadataMap.get(coinType);
        if (dynamicData?.decimals !== undefined) {
            return dynamicData.decimals;
        }
        throw new Error(`No decimal places found for coin type: ${coinType}. Ensure the coin metadata is properly initialized.`);
    }
    /**
     * Gets whether a coin type is pyth sponsored
     * Uses dynamic data fetched from GraphQL API
     */
    getPythSponsored(coinType) {
        const dynamicData = this.coinMetadataMap.get(coinType);
        if (dynamicData?.pythSponsored !== undefined) {
            return dynamicData.pythSponsored;
        }
        return false;
    }
}
//# sourceMappingURL=client.js.map